// Llamadas de Administración · {Tiendas y zonas} (plan 2026-10-30 §2.4,
// T-1iyh). Viven acá y no en lib/api/endpoints/ porque son las de esta
// sección y nada más las usa: la pantalla lista TODO el activo de la
// organización para editarlo, mientras que /tiendas lee el tablero
// facility y /cartera pagina.
//
// Las rutas son las que ya existen en core-api:
//   GET  /assets            (paginado por cursor, tope 200 por página)
//   POST /assets            (alta individual; la pieza backend de esta
//                            ronda la abre al rol orgAdmin de la receta)
//   PUT  /assets/:id        (unit, address, assetGroupId, notes, attributes)
//   GET  /asset-groups      ({ items } con el conteo de activos por grupo)
//   POST /asset-groups      (name, kind, supervisorUserId)
//   PUT  /asset-groups/:id  (name | supervisorUserId + replaceSupervisor)

import type {
  AssetCreateBody,
  AssetGroupCreateBody,
  AssetGroupListItem,
  AssetGroupListResponseV2,
  AssetGroupUpdateBody,
  AssetUpdateBody,
  BrandSummary,
} from "@bitacorapro/api-types";
import { request } from "../../../lib/api/client";

/**
 * Una fila de GET /assets tal como la devuelve la ruta: la fila de la
 * tabla completa más `brand` (marca efectiva, ADR-0033 §C1).
 *
 * Tipo propio y sin parse de ingreso a propósito: `assetListItemSchema`
 * (api-types) declara `address` obligatoria y no conoce
 * `storeManagerUserId`, pero la ruta devuelve `getTableColumns(assets)`
 * entero —`address` es NULL en muchas tiendas de facility (ADR-0020
 * fase 6) y el jefe de tienda viaja en `storeManagerUserId`. Parsear con
 * ese schema tiraría la columna que esta pantalla necesita y fallaría en
 * las direcciones vacías. Cuando api-types incorpore esos campos, este
 * tipo se reemplaza por el suyo.
 */
export type AdminAssetRow = {
  id: string;
  organizationId: string;
  assetGroupId: string | null;
  unit: string | null;
  address: string | null;
  notes?: string | null;
  attributes: Record<string, unknown>;
  brandId?: string | null;
  storeManagerUserId?: string | null;
  supervisorUserId?: string | null;
  brand?: BrandSummary | null;
  createdAt: string;
};

type AssetListPage = { items: AdminAssetRow[]; nextCursor?: string };

/** Tope del `limit` de GET /assets (assetListQuery, api-types). */
const PAGE_LIMIT = 200;
/** Cota de páginas por si un cursor se repitiera: 50 × 200 = 10 000
 *  tiendas, muy por encima de cualquier cadena real. */
const MAX_PAGES = 50;

export const adminAssetsApi = {
  // Recorre el cursor hasta agotarlo: la tabla de administración muestra
  // todas las tiendas de la organización (el gerente ve el alcance
  // completo) y las busca/ordena en el cliente.
  listAll: async (): Promise<AdminAssetRow[]> => {
    const all: AdminAssetRow[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const qs = new URLSearchParams({ limit: String(PAGE_LIMIT) });
      if (cursor) qs.set("cursor", cursor);
      const res = await request<AssetListPage>("GET", "core", `/assets?${qs.toString()}`);
      all.push(...res.items);
      if (!res.nextCursor || res.nextCursor === cursor) break;
      cursor = res.nextCursor;
    }
    return all;
  },
  create: (body: AssetCreateBody): Promise<AdminAssetRow> =>
    request<AdminAssetRow>("POST", "core", "/assets", { body }),
  update: (id: string, body: AssetUpdateBody): Promise<AdminAssetRow> =>
    request<AdminAssetRow>("PUT", "core", `/assets/${encodeURIComponent(id)}`, { body }),
};

export type AdminAssetGroupRow = AssetGroupListItem;

export const adminAssetGroupsApi = {
  list: async (): Promise<AdminAssetGroupRow[]> => {
    const res = await request<AssetGroupListResponseV2>("GET", "core", "/asset-groups");
    return res.items;
  },
  create: (body: AssetGroupCreateBody): Promise<{ id: string; name: string }> =>
    request<{ id: string; name: string }>("POST", "core", "/asset-groups", { body }),
  update: (id: string, body: AssetGroupUpdateBody): Promise<{ id: string; name: string }> =>
    request<{ id: string; name: string }>(
      "PUT",
      "core",
      `/asset-groups/${encodeURIComponent(id)}`,
      { body }
    ),
};
